import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MessageCircle, Gift, Sparkles, Package, Copy, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/contact";

const T = {
  fr: {
    hello: "Bonjour",
    sub: "Votre espace client Smart Telecom Advisor",
    pack: "Votre pack",
    noPack: "Votre offre sur mesure est en préparation avec Sam.",
    savings: "Votre économie énergie estimée",
    perMonth: "/mois",
    referral: "Votre code parrainage",
    referralText: "Partagez votre nom avec vos proches : chaque référence qui souscrit un produit mobile Orange vous offre −5% par mois à vie sur votre abonnement mobile. 20 références = 100% de réduction sur un des numéros de votre facture !",
    share: "Partager sur WhatsApp",
    copy: "Copier mon lien",
    copied: "Lien copié !",
    call: "Une question ? Appelez Sam",
    shareMsg: "−5% par mois, à vie, sur votre abonnement mobile Orange grâce à Sam de Smart Telecom Advisor ! Donnez simplement mon nom",
    leaderboard: "Top parrains",
    refs: "filleul(s)",
    trackerTitle: "Votre progression parrainage",
    trackerRefs: "filleul(s) converti(s)",
    trackerDiscount: "réduction actuelle",
    trackerNext: "Plus que {n} parrainage(s) pour −{d}% !",
    trackerMax: "Bravo — palier maximum atteint : 100% de réduction sur un de vos numéros ! 🎉",
    notFound: "Lien invalide ou expiré.",
  },
  en: {
    hello: "Hello",
    sub: "Your Smart Telecom Advisor client space",
    pack: "Your pack",
    noPack: "Your tailor-made offer is being prepared with Sam.",
    savings: "Your estimated energy savings",
    perMonth: "/mo",
    referral: "Your referral code",
    referralText: "Share your name with friends: each reference who takes an Orange mobile product gives you −5% per month for life on your mobile subscription. 20 references = 100% off one of the numbers on your bill!",
    share: "Share on WhatsApp",
    copy: "Copy my link",
    copied: "Link copied!",
    call: "A question? Call Sam",
    shareMsg: "−5% per month, for life, on your Orange mobile subscription thanks to Sam from Smart Telecom Advisor! Simply give my name",
    leaderboard: "Top referrers",
    refs: "referral(s)",
    trackerTitle: "Your referral progress",
    trackerRefs: "converted referral(s)",
    trackerDiscount: "current discount",
    trackerNext: "Only {n} referral(s) to go for −{d}%!",
    trackerMax: "Congrats — top tier reached: 100% off one of your numbers! 🎉",
    notFound: "Invalid or expired link.",
  },
};

export default function ClientPortal() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/portal/${id}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [id]);

  const t = T[data?.lang === "en" ? "en" : "fr"];
  const refUrl = `${window.location.origin}/?ref=${encodeURIComponent(data?.referral_name || "")}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(`${t.shareMsg} « ${data?.referral_name} ». ${refUrl}`)}`;

  const copyLink = async () => {
    await navigator.clipboard.writeText(refUrl);
    toast.success(t.copied);
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="client-portal">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="w-full max-w-lg bg-[#111111] border border-white/10 p-8 md:p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <Logo size={36} />
          <span className="font-display font-semibold tracking-tight">
            Smart <span className="text-[#FF7900]">Telecom</span> Advisor
          </span>
        </div>
        {error && <p className="text-white/50" data-testid="portal-error">{T.fr.notFound}</p>}
        {data && (
          <>
            <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-1" data-testid="portal-hello">
              {t.hello} {data.first_name} !
            </h1>
            <p className="text-sm text-white/50 mb-8">{t.sub}</p>
            <div className="border border-white/10 bg-[#0a0a0a] p-5 mb-4" data-testid="portal-pack">
              <p className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#FF7900] mb-2">
                <Package className="w-3.5 h-3.5" /> {t.pack}
              </p>
              <p className="text-sm text-white/80">{data.pack || t.noPack}</p>
            </div>
            {data.savings != null && (
              <div className="border border-emerald-500/30 bg-emerald-500/5 p-5 mb-4" data-testid="portal-savings">
                <p className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-400 mb-2">
                  <Sparkles className="w-3.5 h-3.5" /> {t.savings}
                </p>
                <p className="font-display text-2xl font-semibold text-emerald-400">
                  €{data.savings}
                  <span className="text-sm font-normal text-white/50">{t.perMonth}</span>
                </p>
              </div>
            )}
            <div className="border border-[#FF7900]/30 bg-[#FF7900]/5 p-5 mb-6" data-testid="portal-referral">
              <p className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#FF7900] mb-2">
                <Gift className="w-3.5 h-3.5" /> {t.referral}
              </p>
              <p className="font-display text-xl font-semibold mb-2">« {data.referral_name} »</p>
              <p className="text-xs text-white/50 leading-relaxed mb-4">{t.referralText}</p>
              <div className="mb-5" data-testid="portal-referral-tracker">
                <p className="text-[11px] uppercase tracking-widest text-white/40 mb-2">{t.trackerTitle}</p>
                <div className="flex items-end gap-6 mb-2">
                  <p className="font-display text-2xl font-semibold" data-testid="portal-referral-count">
                    {data.referrals}
                    <span className="text-xs font-normal text-white/45 ml-1.5">{t.trackerRefs}</span>
                  </p>
                  <p className="font-display text-2xl font-semibold text-[#FF7900]" data-testid="portal-referral-discount">
                    −{data.discount}%
                    <span className="text-xs font-normal text-white/45 ml-1.5">{t.trackerDiscount}</span>
                  </p>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                  <div
                    className="h-full bg-gradient-to-r from-[#FF7900] to-[#FBBC04] rounded-full transition-[width] duration-700"
                    style={{ width: `${Math.min(100, (data.referrals / (data.next_milestone || 20)) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-white/60" data-testid="portal-referral-next">
                  {data.next_milestone
                    ? t.trackerNext
                        .replace("{n}", data.next_milestone - data.referrals)
                        .replace("{d}", Math.min(100, data.next_milestone * 5))
                    : t.trackerMax}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="portal-share-whatsapp"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-white hover:text-black text-white text-xs font-semibold px-4 py-2.5 rounded-full transition-colors duration-300"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> {t.share}
                </a>
                <button
                  onClick={copyLink}
                  data-testid="portal-copy-link"
                  className="inline-flex items-center gap-2 border border-white/20 hover:border-[#FF7900] hover:text-[#FF7900] text-xs font-semibold px-4 py-2.5 rounded-full transition-colors duration-300"
                >
                  <Copy className="w-3.5 h-3.5" /> {t.copy}
                </button>
              </div>
            </div>
            {data.leaderboard?.length > 0 && (
              <div className="border border-white/10 bg-[#0a0a0a] p-5 mb-6" data-testid="portal-leaderboard">
                <p className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-amber-400 mb-3">
                  <Trophy className="w-3.5 h-3.5" /> {t.leaderboard}
                </p>
                {data.leaderboard.map((r, i) => (
                  <p key={r.name} className="flex justify-between text-sm py-1" data-testid={`portal-leader-${i}`}>
                    <span className="text-white/80">
                      <span className={`font-semibold mr-2 ${i === 0 ? "text-amber-400" : i === 1 ? "text-white/60" : "text-amber-700"}`}>#{i + 1}</span>
                      {r.name}
                    </span>
                    <span className="text-white/40">{r.count} {t.refs}</span>
                  </p>
                ))}
              </div>
            )}
            <a
              href={PHONE_TEL}
              data-testid="portal-call"
              className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#FF7900] transition-colors duration-300"
            >
              <Phone className="w-4 h-4" /> {t.call} · {PHONE_DISPLAY}
            </a>
          </>
        )}
      </motion.div>
    </main>
  );
}
